import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useRegister, useTournament, useTournamentLive } from '../api/hooks';
import { Loader } from '../components/StateViews';
import { Avatar } from '../components/Avatar';
import { BlueHeader, HeaderBack } from '../components/ScreenShell';
import { MascotWatermark } from '../components/Watermark';
import { backButton, haptics } from '../telegram';
import { track } from '../analytics';
import { shortDate } from '../format';
import type { LivePlayer } from '../api/types';

const n = (x: number) => Math.round(x).toLocaleString('ru-RU');

function fmt(sec: number): string {
  const s = Math.max(0, sec);
  const m = Math.floor(s / 60);
  return `${String(m).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

/** Как на дисплее Korin: H:MM:SS от часа и выше, иначе MM:SS. */
function fmtHours(sec: number): string {
  const s = Math.max(0, sec);
  const h = Math.floor(s / 3600);
  if (h === 0) return fmt(s);
  const m = Math.floor((s % 3600) / 60);
  return `${h}:${String(m).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

/** Live tournament — real data published by Korin (polled every 2s; clock ticks locally). */
export function LiveTournamentScreen() {
  const { id } = useParams();
  const tid = Number(id);
  const navigate = useNavigate();
  const { data: t } = useTournament(tid);
  const { data: live, isLoading } = useTournamentLive(tid);
  const register = useRegister(tid);
  const [tab, setTab] = useState<'overview' | 'busts'>('overview');
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    const back = () => navigate(-1);
    backButton.show(back);
    return () => backButton.hide(back);
  }, [navigate]);

  useEffect(() => {
    const tmr = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(tmr);
  }, []);

  useEffect(() => {
    if (Number.isFinite(tid)) track('live_view', { gameId: tid });
  }, [tid]);

  const remaining = useMemo(() => {
    if (!live) return 0;
    if (live.isPaused || !live.levelEndsAt) return live.timeLeftSec;
    return Math.max(0, Math.round((Date.parse(live.levelEndsAt) - nowMs) / 1000));
  }, [live, nowMs]);

  // Late-reg countdown mirrors the level clock: absolute deadline while running,
  // frozen snapshot seconds while paused.
  const lateRegRemaining = useMemo(() => {
    if (!live || !live.lateRegOpen) return 0;
    if (live.isPaused || !live.lateRegEndsAt) return live.lateRegTimeLeftSec;
    return Math.max(0, Math.round((Date.parse(live.lateRegEndsAt) - nowMs) / 1000));
  }, [live, nowMs]);

  // «Перерыв через» — тот же паттерн (тикает от абсолютного времени, на паузе замирает).
  const breakRemaining = useMemo(() => {
    if (!live || live.breakInSec <= 0) return 0;
    if (live.isPaused || !live.breakEndsAt) return live.breakInSec;
    return Math.max(0, Math.round((Date.parse(live.breakEndsAt) - nowMs) / 1000));
  }, [live, nowMs]);

  const busted = useMemo<LivePlayer[]>(
    () =>
      (live?.players ?? [])
        .filter((p) => p.status === 'OUT')
        .sort((a, b) => (a.finalPosition ?? 9999) - (b.finalPosition ?? 9999)),
    [live],
  );

  // Сетка мест: кто уже занял место — показываем прямо в строке места.
  const playerByPlace = useMemo<Map<number, LivePlayer>>(() => {
    const map = new Map<number, LivePlayer>();
    for (const p of busted) if (p.finalPosition != null) map.set(p.finalPosition, p);
    return map;
  }, [busted]);

  // Единый список «Места и очки»: вся призовая сетка (включая ещё не занятые
  // места) + вылеты вне призов. У занятого места очки берём персональные
  // (с баунти), у свободного — из сетки.
  const prizeRows = useMemo<{ place: number; points: number; player: LivePlayer | null }[]>(() => {
    const prizes = live?.prizes ?? [];
    const rows = prizes.map((pr) => {
      const player = playerByPlace.get(pr.place) ?? null;
      return { place: pr.place, points: player?.points || pr.points, player };
    });
    const maxPrize = prizes.length > 0 ? prizes[prizes.length - 1].place : 0;
    for (const p of busted) {
      if (p.finalPosition != null && p.finalPosition > maxPrize) {
        rows.push({ place: p.finalPosition, points: p.points, player: p });
      }
    }
    rows.sort((a, b) => a.place - b.place);
    return rows;
  }, [live, playerByPlace, busted]);

  // Chip leaders — ONLY players the operator actually filled in (real entered
  // stack > 0). Per-table leader when tables are set, otherwise overall top stacks.
  const leaders = useMemo<{ label: string; p: LivePlayer; lead: boolean }[]>(() => {
    const filled = (live?.players ?? []).filter((p) => p.status === 'IN_GAME' && p.stack > 0);
    if (filled.length === 0) return [];
    const hasTables = filled.some((p) => p.tableNo != null);
    let list: { label: string; p: LivePlayer }[];
    if (hasTables) {
      const byTable = new Map<number, LivePlayer>();
      for (const p of filled) {
        if (p.tableNo == null) continue;
        const cur = byTable.get(p.tableNo);
        if (!cur || p.stack > cur.stack) byTable.set(p.tableNo, p);
      }
      list = [...byTable.entries()]
        .sort((a, b) => a[0] - b[0])
        .map(([table, p]) => ({ label: `Стол ${table}`, p }));
    } else {
      list = [...filled]
        .sort((a, b) => b.stack - a.stack)
        .slice(0, 8)
        .map((p, i) => ({ label: `#${i + 1}`, p }));
    }
    const maxStack = Math.max(...list.map((x) => x.p.stack));
    return list.map((x) => ({ ...x, lead: x.p.stack === maxStack }));
  }, [live]);

  if (isLoading) return <Loader />;

  const title = t?.title ?? t?.format ?? 'Турнир';

  if (!live || !live.live) {
    return (
      <div>
        <BlueHeader mascot="tr">
          <div className="bhead-top" style={{ marginBottom: 14 }}>
            <HeaderBack onClick={() => navigate(-1)} />
            <div className="bhead-title bhead-title--sm" style={{ flex: 1 }}>{title}</div>
          </div>
        </BlueHeader>
        <div className="content">
          <div className="card" style={{ padding: 20, textAlign: 'center' }}>
            <div className="muted">
              {live?.status === 'finished' ? 'Турнир завершён.' : 'Турнир ещё не запущен.'}
            </div>
            {live?.status === 'finished' && (
              <Link to={`/tournaments/${tid}/results`} className="btn btn--ghost" style={{ marginTop: 12 }}>
                Результаты турнира →
              </Link>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <BlueHeader wave={tab === 'busts'}>
        <div className="bhead-top" style={{ marginBottom: 14 }}>
          <HeaderBack onClick={() => navigate(-1)} />
          <div className="bhead-title bhead-title--sm" style={{ flex: 1 }}>{title}</div>
          <span className="hchip" style={{ letterSpacing: '0.1em', fontWeight: 700, fontSize: 9 }}>
            <i className="dot dot--live" /> {live.isPaused ? 'ПАУЗА' : 'В ИГРЕ'}
          </span>
        </div>
        <div className="bhead-meta" style={{ marginTop: 0 }}>
          {t ? `${shortDate(t.startTime)} · ${t.format ?? 'NLH'}` : ''}
        </div>
      </BlueHeader>

      <div className="content">
      <div className="segmented">
        <button className={tab === 'overview' ? 'active' : ''} onClick={() => setTab('overview')}>
          Обзор
        </button>
        <button className={tab === 'busts' ? 'active' : ''} onClick={() => setTab('busts')}>
          Вылеты и места
        </button>
      </div>

      {tab === 'overview' ? (
        <>
          {/* Единственный тёмный блок в светлой теме (README §10) */}
          <div className="live-timer">
            <MascotWatermark pos="br" />
            <div className="lvl">
              {live.isBreak ? 'Перерыв' : `Уровень ${live.level}`} · до повышения
            </div>
            <div className={`clock${!live.isPaused && remaining <= 60 ? ' clock--last-min' : ''}`}>
              {fmt(remaining)}
            </div>
            <div className="blinds">
              <span>{n(live.smallBlind)} / {n(live.bigBlind)}</span>
              {live.ante > 0 ? <span className="ante">анте {n(live.ante)}</span> : null}
            </div>
            {live.nextSmallBlind > 0 ? (
              <div className="note">
                Следующий уровень: {n(live.nextSmallBlind)} / {n(live.nextBigBlind)}
              </div>
            ) : null}
            {live.lateRegOpen && lateRegRemaining > 0 ? (
              <div className="note" style={{ marginTop: 4 }}>
                До конца поздней регистрации: {fmtHours(lateRegRemaining)}
              </div>
            ) : null}
          </div>

          {/* Пока открыта поздняя регистрация — можно записаться прямо отсюда. */}
          {live.lateRegOpen && lateRegRemaining > 0 && t?.registrationOpen && !t.registeredByMe ? (
            <button
              className="btn"
              style={{ display: 'block', width: '100%', margin: '0 0 10px' }}
              disabled={register.isPending}
              onClick={async () => {
                track('register_click', { gameId: tid, from: 'live' });
                try {
                  await register.mutateAsync();
                  haptics.success();
                } catch {
                  haptics.error();
                }
              }}
            >
              {register.isPending ? 'Записываем…' : 'Записаться в турнир'}
            </button>
          ) : null}
          {live.lateRegOpen && lateRegRemaining > 0 && t?.registeredByMe ? (
            <div className="muted tiny" style={{ textAlign: 'center', margin: '0 0 10px' }}>
              Вы записаны на этот турнир
            </div>
          ) : null}

          <div className="hero-stats" style={{ margin: '0 0 10px' }}>
            <div className="hero-stat">
              <div className="v">{live.playersIn}/{live.playersTotal}</div>
              <div className="l">игроки</div>
            </div>
            <div className="hero-stat">
              <div className="v accent">{n(live.avgStack)}</div>
              <div className="l">ср. стек</div>
            </div>
            <div className="hero-stat">
              <div className="v">{n(live.chipsInPlay)}</div>
              <div className="l">фишек в игре</div>
            </div>
          </div>
          <div className="hero-stats hero-stats--single" style={{ margin: '0 0 18px' }}>
            <div className="hero-stat">
              <div className="v">{breakRemaining > 0 ? fmtHours(breakRemaining) : '—'}</div>
              <div className="l">перерыв через</div>
            </div>
          </div>

          <div className="zone-divider">Чип-лидеры<span /></div>
          {leaders.length === 0 ? (
            <div className="muted tiny" style={{ padding: '2px 6px 8px' }}>
              Стеки ещё не введены оператором.
            </div>
          ) : (
            <div className="card" style={{ padding: '4px 6px' }}>
              {leaders.map(({ label, p, lead }, i) => {
                const row = (
                  <>
                    <span className="tbl">{label}</span>
                    <Avatar name={p.name} src={p.photoUrl} size={26} variant={1} />
                    <span className="nm">{p.name}{p.isMe ? ' · Вы' : ''}</span>
                    <span className="stack">{n(p.stack)}</span>
                  </>
                );
                // Клик по игроку → его публичный профиль. Ручные игроки Korin
                // без привязки к базе (playerId=null) остаются некликабельными.
                return p.playerId != null ? (
                  <Link
                    className={`chip-row ${lead ? 'lead' : ''}`}
                    key={`${p.playerId}-${i}`}
                    to={`/players/${p.playerId}`}
                    onClick={() => track('player_open', { playerId: p.playerId, from: 'live' })}
                  >
                    {row}
                  </Link>
                ) : (
                  <div className={`chip-row ${lead ? 'lead' : ''}`} key={`x-${i}`}>
                    {row}
                  </div>
                );
              })}
            </div>
          )}
        </>
      ) : (
        <>
          <div className="hero-stats" style={{ margin: '4px 0 14px' }}>
            <div className="hero-stat">
              <div className="v green">{live.playersIn}</div>
              <div className="l">в игре</div>
            </div>
            <div className="hero-stat">
              <div className="v">{busted.length}</div>
              <div className="l">выбыли</div>
            </div>
            <div className="hero-stat">
              <div className="v accent">{live.knockouts}</div>
              <div className="l">нокаутов</div>
            </div>
          </div>
          {/* Единый блок «Места и очки»: вся призовая сетка видна сразу (даже
              до первого вылета); занятые места показывают игрока, после сетки —
              вылеты вне призов. Отдельного списка выбывших больше нет. */}
          {prizeRows.length === 0 ? (
            <div className="muted tiny">Пока нет данных о местах.</div>
          ) : (
            <>
              <div className="zone-divider">Места и очки<span /></div>
              <div className="card" style={{ padding: '4px 6px' }}>
                {prizeRows.map((r) => {
                  const e = r.player;
                  const row = (
                    <>
                      <span className="pl">{r.place}.</span>
                      {e ? (
                        <>
                          <Avatar name={e.name} src={e.photoUrl} size={26} variant={1} />
                          <span className="nm">
                            {e.name}
                            {e.isMe ? ' · Вы' : ''}
                            {/* №17: нокауты видны рядом — игроки не понимали,
                                что баунти уже входит в сумму очков */}
                            {e.knockouts > 0 ? (
                              <span className="nm-ko"> · {e.knockouts} КО</span>
                            ) : null}
                          </span>
                        </>
                      ) : (
                        <span className="nm muted" />
                      )}
                      {r.points > 0 ? <span className="pts">+{n(r.points)}</span> : null}
                    </>
                  );
                  // «out» = место уже занято вылетевшим (строка гаснет);
                  // свободные места остаются яркими — они ещё разыгрываются.
                  return e?.playerId != null ? (
                    <Link
                      className={`bust-row out ${e.isMe ? 'me' : ''}`}
                      key={`p-${r.place}`}
                      to={`/players/${e.playerId}`}
                      onClick={() => track('player_open', { playerId: e.playerId, from: 'live' })}
                    >
                      {row}
                    </Link>
                  ) : (
                    <div className={`bust-row ${e ? 'out' : ''} ${e?.isMe ? 'me' : ''}`} key={`p-${r.place}`}>
                      {row}
                    </div>
                  );
                })}
              </div>
              <div className="tiny muted" style={{ marginTop: 8 }}>
                Места без имени ещё разыгрываются.
              </div>
            </>
          )}
        </>
      )}
      </div>
    </div>
  );
}
